// #9: Guest history by mobile — a self-contained badge + popup. Drop it next to
// any mobile-number field (booking form, Guest Report). It fetches the guest's
// past bookings from /api/bookingledger/guest-history/{mobile}, shows a
// "Previous Visits: N" chip, and opens a searchable history table (with the
// uploaded ID-proof image) on click. Read-only — never touches booking logic.
import React, { useEffect, useState } from 'react';
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TextField,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import config from '../../config';

// The running company - guest history is ALWAYS scoped to it, so one hotel can
// never see another company's guests. Resolved here (not passed in) so no
// caller can forget it.
const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');

export default function GuestHistoryBadge({ mobile, onBookAgain }) {
  const [data, setData] = useState({ count: 0, bookings: [] });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const m = (mobile || '').toString().trim();
    if (m.length !== 10 || !company?.id) {
      setData({ count: 0, bookings: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await fetch(
          `${config.API_BASE_URL}/bookingledger/guest-history/${m}?companyId=${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled)
          setData({ count: d.count || 0, bookings: d.bookings || [] });
      } catch {
        /* ignore — badge just stays hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mobile]);

  if (!data.count) return null;

  // Images are served from the API ROOT, not under /api — strip the /api
  // suffix the same way the rest of the app does (Bookings/Category).
  const imgSrc = (p) =>
    !p
      ? ''
      : p.startsWith('http')
        ? p
        : `${config.API_BASE_URL.replace(/\/api$/, '')}${p}`;

  const filtered = data.bookings.filter((b) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [b.guestName, b.hotel, b.rooms, b.bookingStatus, String(b.bookingId)]
      .map((v) => (v || '').toString().toLowerCase())
      .some((v) => v.includes(q));
  });

  const headers = [
    'Booking',
    'Booked On',
    'Check In',
    'Check Out',
    'Hotel',
    'Room',
    'Days',
    'Room Price',
    'Discount',
    'Voucher',
    'Total',
    'Payment',
    'Status',
    'ID Proof',
  ];

  return (
    <>
      <Chip
        icon={<HistoryIcon />}
        color="primary"
        size="small"
        label={`Previous Visits: ${data.count}`}
        onClick={() => setOpen(true)}
        sx={{ cursor: 'pointer', fontWeight: 700 }}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth>
        <DialogTitle
          sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
          Guest History — {mobile} ({data.count} visit
          {data.count === 1 ? '' : 's'})
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            placeholder="Search booking / hotel / room / status"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                  {onBookAgain && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.bookingId} hover>
                    <TableCell>#{b.bookingId}</TableCell>
                    <TableCell>
                      {b.bookingDate
                        ? new Date(b.bookingDate).toLocaleDateString('en-IN')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {b.checkIn
                        ? new Date(b.checkIn).toLocaleDateString('en-IN')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {b.checkOut
                        ? new Date(b.checkOut).toLocaleDateString('en-IN')
                        : '-'}
                    </TableCell>
                    <TableCell>{b.hotel}</TableCell>
                    <TableCell>{b.rooms || '-'}</TableCell>
                    <TableCell>{b.totalDays}</TableCell>
                    <TableCell>₹{b.roomPrice}</TableCell>
                    <TableCell>{b.discount || 0}</TableCell>
                    <TableCell>{b.voucher || '-'}</TableCell>
                    <TableCell>₹{Number(b.totalAmount || 0).toFixed(0)}</TableCell>
                    <TableCell>{b.paymentStatus}</TableCell>
                    <TableCell>{b.bookingStatus}</TableCell>
                    <TableCell>
                      {imgSrc(b.aadharFront) ? (
                        <a
                          href={imgSrc(b.aadharFront)}
                          target="_blank"
                          rel="noreferrer">
                          <img
                            src={imgSrc(b.aadharFront)}
                            alt="ID"
                            style={{
                              width: 44,
                              height: 30,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    {onBookAgain && (
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            onBookAgain(b);
                            setOpen(false);
                          }}>
                          Book Again
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
